/**
 * Task checklist aggregation.
 *
 * Claude Code retired the `TodoWrite` tool, which rewrote the entire checklist on every call,
 * in favour of per-task CRUD: `TaskCreate`, `TaskUpdate`, `TaskList`, `TaskGet`. Because a single
 * turn may now only *mutate* a checklist created in an earlier turn, callers must replay the calls
 * in order and keep the running state rather than reading a snapshot from the latest call.
 *
 * Observed tool shapes (Claude Code 2.1.258):
 *   TaskCreate  input  { subject, description?, activeForm? }
 *               result "Task #1 created successfully: Write parser"
 *   TaskUpdate  input  { taskId | taskIds, status }
 *               result "Updated task #1 status"
 *   TaskList    result "#1 [in_progress] Write parser\n#2 [pending] Add tests"
 *
 * The retired `TodoWrite` shape ({ todos: [{ content, status, activeForm }] }) is still replayed
 * so transcripts recorded before the migration keep rendering their checklist.
 */

export type TodoStatus = 'pending' | 'in_progress' | 'completed' | 'interrupted'

export interface TodoItem {
  content: string
  status: TodoStatus
  activeForm?: string
  /** Task id assigned by the SDK, used for stable list keys. Absent for legacy TodoWrite data. */
  id?: string
}

/**
 * Tool names that mutate or report checklist state.
 *
 * `TaskGet` is excluded: it only reads a single task, and `TaskList` already recovers tasks
 * created outside the messages in scope. `TodoWrite` is included so transcripts recorded before
 * the Task* migration still render their checklist.
 */
export const TASK_CHECKLIST_TOOLS = new Set([
  'TaskCreate',
  'TaskUpdate',
  'TaskList',
  'TodoWrite',
])

const VALID_STATUSES = new Set<TodoStatus>(['pending', 'in_progress', 'completed', 'interrupted'])

const CREATE_RESULT = /Task #(\d+) created/i
const LIST_ROW = /^#(\d+)\s+\[([a-z_]+)\]\s+(.*)$/

function toStatus(value: unknown): TodoStatus | undefined {
  return typeof value === 'string' && VALID_STATUSES.has(value as TodoStatus)
    ? (value as TodoStatus)
    : undefined
}

export interface TaskChecklistCall {
  toolName?: string
  toolInput?: Record<string, unknown>
  /**
   * The tool result text. `Message` carries it here, leaving `content` empty; turn activities
   * flatten it into `content`. Callers may pass either shape.
   */
  toolResult?: string
  content?: unknown
}

function resultText(call: TaskChecklistCall): string {
  const raw = call.toolResult ?? call.content
  return typeof raw === 'string' ? raw : ''
}

/**
 * Replays task tool calls into a checklist, preserving insertion order.
 *
 * Kept stateful (rather than a pure reduce) so `groupMessagesByTurn` can carry the checklist
 * across turn boundaries and snapshot it per turn.
 */
export class TaskChecklist {
  private readonly tasks = new Map<string, TodoItem>()
  private syntheticId = 0

  apply(call: TaskChecklistCall): void {
    const { toolName, toolInput } = call
    if (!toolName) return

    switch (toolName) {
      case 'TaskCreate': {
        const subject = toolInput?.subject
        if (typeof subject !== 'string') return
        const match = CREATE_RESULT.exec(resultText(call))
        const id = match?.[1] ?? `synthetic-${++this.syntheticId}`
        const activeForm = toolInput?.activeForm
        this.tasks.set(id, {
          id,
          content: subject,
          status: 'pending',
          activeForm: typeof activeForm === 'string' ? activeForm : undefined,
        })
        return
      }

      case 'TaskUpdate': {
        const status = toStatus(toolInput?.status)
        if (!status) return
        const ids = Array.isArray(toolInput?.taskIds)
          ? toolInput.taskIds
          : [toolInput?.taskId]
        for (const rawId of ids) {
          const id = rawId == null ? '' : String(rawId)
          const existing = this.tasks.get(id)
          if (existing) this.tasks.set(id, { ...existing, status })
        }
        return
      }

      case 'TaskList': {
        // A listing is the authoritative view, and is the only way to recover tasks created
        // before the messages currently in scope. It omits activeForm, so merge rather than replace.
        for (const line of resultText(call).split('\n')) {
          const match = LIST_ROW.exec(line.trim())
          if (!match) continue
          const [, id, rawStatus, subject = ''] = match
          if (!id) continue
          const status = toStatus(rawStatus) ?? 'pending'
          const existing = this.tasks.get(id)
          this.tasks.set(id, {
            id,
            content: subject,
            status,
            activeForm: existing?.activeForm,
          })
        }
        return
      }

      case 'TodoWrite': {
        // Legacy pre-Task* transcripts: the whole checklist arrives as a snapshot on every call,
        // so replace rather than merge. These items carry no task id.
        const todos = toolInput?.todos
        if (!Array.isArray(todos)) return
        this.tasks.clear()
        todos.forEach((todo, index) => {
          const item = todo as Record<string, unknown> | null
          const content = item?.content
          if (typeof content !== 'string') return
          const activeForm = item?.activeForm
          this.tasks.set(`legacy-${index}`, {
            content,
            status: toStatus(item?.status) ?? 'pending',
            activeForm: typeof activeForm === 'string' ? activeForm : undefined,
          })
        })
        return
      }

      default:
        return
    }
  }

  get size(): number {
    return this.tasks.size
  }

  /** Current checklist, in creation order. */
  snapshot(): TodoItem[] {
    return Array.from(this.tasks.values(), (task) => ({ ...task }))
  }
}

/** Convenience wrapper for callers that just need the final state of a message list. */
export function aggregateTaskChecklist(calls: readonly TaskChecklistCall[]): TodoItem[] {
  const checklist = new TaskChecklist()
  for (const call of calls) checklist.apply(call)
  return checklist.snapshot()
}
